import { isPage, isSection } from "../../common/utils";

/**
 * Recursively finds the first visible page inside a nav item (page or section).
 * Returns null if nothing found.
 */
export function findFirstValidPageRecursive(element) {
  if (!element) return null;

  if (isPage(element)) {
    const navItem = IdealStudio.State.navItemsById[element];
    if (navItem && !navItem.hidden) {
      return element;
    }
    return null;
  }

  if (isSection(element)) {
    const sectionNavItem = IdealStudio.State.navItemsById[element];
    if (sectionNavItem && sectionNavItem.children && sectionNavItem.children.length > 0) {
      for (let i = 0; i < sectionNavItem.children.length; i++) {
        const childId = sectionNavItem.children[i];
        const childNavItem = IdealStudio.State.navItemsById[childId];

        if (!childNavItem || childNavItem.hidden) {
          continue;
        }

        if (isPage(childId)) {
          return childId;
        }

        if (isSection(childId)) {
          const foundPage = findFirstValidPageRecursive(childId);
          if (foundPage) {
            return foundPage;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Returns all visible pages (recursively) within a section.
 */
export function getAllPagesInSectionRecursive(sectionId) {
  const pages = [];
  if (!sectionId || !isSection(sectionId)) {
    return pages;
  }

  const sectionNavItem = IdealStudio.State.navItemsById[sectionId];
  if (!sectionNavItem || !sectionNavItem.children || sectionNavItem.children.length === 0) {
    return pages;
  }

  for (let i = 0; i < sectionNavItem.children.length; i++) {
    const childId = sectionNavItem.children[i];
    const childNavItem = IdealStudio.State.navItemsById[childId];

    if (!childNavItem || childNavItem.hidden) {
      continue;
    }

    if (isPage(childId)) {
      pages.push(childId);
    } else if (isSection(childId)) {
      const nestedPages = getAllPagesInSectionRecursive(childId);
      pages.push(...nestedPages);
    }
  }

  return pages;
}

/**
 * Returns the flat index of pageId in navItemsIds, -1 if not found.
 */
export function findPageIndexInNavItems(pageId) {
  if (!pageId) return -1;

  const navItemsIds = IdealStudio.State.navItemsIds;
  const navItems = IdealStudio.State.navItemsById;

  const index = navItemsIds.indexOf(pageId);
  if (index !== -1) {
    return index;
  }

  for (let i = 0; i < navItemsIds.length; i++) {
    const item = navItemsIds[i];
    const navItem = navItems[item];
    if (!navItem || navItem.hidden) continue;

    const foundPage = findFirstValidPageRecursive(item);
    if (foundPage === pageId) {
      return i;
    }
  }

  return -1;
}

/**
 * Returns the next visible page after currentPageId, or null if at the end.
 */
export function getNextValidPage(currentPageId) {
  if (!currentPageId) return null;

  const navItemsIds = IdealStudio.State.navItemsIds;
  const navItems = IdealStudio.State.navItemsById;

  const currentIndex = findPageIndexInNavItems(currentPageId);
  if (currentIndex === -1 || currentIndex >= navItemsIds.length - 1) {
    return null;
  }

  for (let i = currentIndex + 1; i < navItemsIds.length; i++) {
    const nextItem = navItemsIds[i];
    const navItem = navItems[nextItem];

    if (!navItem || navItem.hidden) {
      continue;
    }

    const foundPage = findFirstValidPageRecursive(nextItem);
    if (foundPage) {
      return foundPage;
    }
  }

  return null;
}

/**
 * Resolves the initial view from the Redux-selected navItem and containedView.
 */
export function getCurrentView(NIselected, CVselected) {
  let navItemSelected = "";
  if (
    IdealStudio.State.navItemsById[NIselected] &&
    !IdealStudio.State.navItemsById[NIselected].hidden
  ) {
    navItemSelected = NIselected;
  }
  return CVselected === 0 ? navItemSelected : CVselected;
}

/**
 * Returns true if the id does not belong to a section.
 */
export function isntSection(page) {
  return page.indexOf("se") === -1;
}

/**
 * Parses a named query parameter from a URL string (defaults to window.location.href).
 */
export function getParameterByName(name, url = window.location.href) {
  name = name.replace(/[\[\]]/g, "\\$&");
  const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}
